<div class="site-card card">
  <div class="site-card__header">
    <div class="site-card__headrow">
    <button
      type="button"
      class="site-card__toggle"
      (click)="toggle()"
      [attr.aria-expanded]="expanded"
      [attr.data-testid]="testidBase + '-toggle'"
    >
      <span
        class="material-icons site-card__chevron"
        [class.site-card__chevron--open]="expanded"
        >keyboard_arrow_down</span
      >
      <span class="site-card__id">
        <ng-container *ngIf="site.siteNumber !== null">#{{ site.siteNumber }}</ng-container>
        <span class="site-card__addr" *ngIf="address.streetAddress">
          · {{ address.streetAddress }}</span
        >
      </span>
    </button>
    <div class="site-card__bulk" *ngIf="!readOnly && hasBulkActions">
      <ng-container *ngFor="let action of bulkActions">
        <button
          *ngIf="bulk[action.action] as set"
          type="button"
          class="btn"
          [class.btn--primary]="action.action !== 'decline'"
          [class.btn--secondary]="action.action === 'decline'"
          [attr.data-testid]="testidBase + '-' + action.suffix"
          [attr.aria-label]="action.label + ' ' + set.eligible.length + ' ' + bulkScope"
          [attr.aria-disabled]="!!disabledReason(action, set.eligible)"
          [matTooltip]="disabledReason(action, set.eligible) ?? ''"
          [matTooltipDisabled]="!disabledReason(action, set.eligible)"
          matTooltipTouchGestures="off"
          (click)="onBulkAction(action.action)"
        >
          {{ action.label }} <span class="site-card__bulk-count">{{ set.eligible.length }}</span>
        </button>
      </ng-container>
    </div>
      <span class="site-card__count">
        <ng-container *ngIf="expanded"
          >{{ productCount }} {{ productCount === 1 ? "product" : "products" }}</ng-container
        >
        <ng-container *ngIf="!expanded">{{ rollupSummary }}</ng-container>
      </span>
    </div>

    <div class="site-card__meta">
      <!-- Left/map column: the map thumbnail and (Active) "Open Workspace"
           button are stacked together here so the button has a stable
           position anchored under the thumbnail, independent of how long the
           address + updated-stamp block below wraps. -->
      <div class="site-card__meta-left">
        <button
          type="button"
          class="site-card__map-trigger"
          (click)="toggleMapModal()"
          matTooltip="View site map"
          matTooltipPosition="above"
          [attr.data-testid]="testidBase + '-map'"
        >
          <ng-container *ngIf="staticMapUrl as mapUrl; else mapIconFallback">
            <img
              [src]="mapUrl"
              [alt]="mapAltText"
              class="site-card__map-thumb"
              loading="lazy"
              decoding="async"
              width="150"
              height="104"
            />
          </ng-container>
          <ng-template #mapIconFallback>
            <span class="material-icons">map</span>
          </ng-template>
        </button>

        <!-- Open Workspace → the multi-product workspace (/active/:id). Shown
             whenever the site has an Active-phase product, so it appears in the
             SAME place on both the Active list and Project Overview (one spot
             for the vendor). Routed via the first active product's site id so
             the workspace — which only shows Active-phase products — lands on a
             genuine active product, not a quote/awarded sibling. -->
        <button
          type="button"
          class="site-card__workspace"
          *ngIf="hasActiveProduct && !readOnly"
          [routerLink]="['/active', openWorkspaceSiteId]"
          [state]="{ workspaceBackType: type }"
          [attr.data-testid]="testidBase + '-workspace'"
          matTooltip="Upload reports and documents, schedule the site visit, and submit your work here."
          matTooltipPosition="above"
        >
          Open Workspace
          <span class="material-icons">chevron_right</span>
        </button>

        <!-- Task 3 (Project History): read-only "View" affordance into the
             record, in the same slot as the Active "Open Workspace" button
             above (mutually exclusive — `readOnly` screens don't pass
             type === 'active'). Same routerLink shape (`['/', type, id]`),
             so this resolves to `/project-history/:id`. -->
        <button
          type="button"
          class="site-card__view"
          *ngIf="readOnly"
          [routerLink]="['/', type, site.site?.id]"
          [attr.data-testid]="testidBase + '-view'"
        >
          <span class="material-icons">visibility</span>
          View
        </button>
      </div>

      <!-- Right/text column: address, updated-stamp, and (Active) comments
           wrap freely amongst themselves without disturbing the button above. -->
      <div class="site-card__meta-right">
        <button
          type="button"
          class="site-card__address-link"
          (click)="toggleMapModal()"
          [attr.data-testid]="testidBase + '-map-link'"
        >
          <span *ngIf="address.streetAddress">{{ address.streetAddress }}, </span
          >{{ address.cityStateZip }}
        </button>
        <span class="site-card__updated" *ngIf="updatedOn"
          >Information Updated {{ updatedOn | date : "MM/dd/yyyy" }}</span
        >
        <span
          class="site-card__comments"
          *ngIf="type === 'active' && commentsCount > 0"
          [routerLink]="['/', type, site.site?.id]"
          [attr.data-testid]="testidBase + '-comments'"
        >
          &#128172; {{ commentsCount }}
        </span>
      </div>
    </div>
  </div>

  <div class="site-card__facts">
    <div class="site-card__fact">
      <span class="site-card__fact-label">Acreage</span>
      <span class="site-card__fact-value">{{ acreage }}</span>
    </div>
    <div class="site-card__fact">
      <span class="site-card__fact-label">Total Building Sq. Ft.</span>
      <span class="site-card__fact-value">{{ totalBuildingSqft }}</span>
    </div>
    <div class="site-card__fact">
      <span class="site-card__fact-label">Building Count</span>
      <span class="site-card__fact-value">{{ buildingCount }}</span>
    </div>
    <div class="site-card__fact">
      <span class="site-card__fact-label">Land Use</span>
      <span class="site-card__fact-value">{{ landUse }}</span>
    </div>
  </div>

  <!-- Table A changes — own callout, mirrors the workspace's
       `.workspace__callout--info` (checklist icon + "Table A" title). -->
  <div class="site-card__callout site-card__callout--info" *ngIf="hasTableAChanges">
    <span class="material-icons">checklist</span>
    <div>
      <p class="site-card__callout-title">Table A</p>
      <div class="site-card__table-a">
        <span class="site-card__table-a-added" *ngIf="tableAItemsAdded"
          >+ {{ tableAItemsAdded }}</span
        >
        <span class="site-card__table-a-removed" *ngIf="tableAItemsRemoved"
          >&minus; {{ tableAItemsRemoved }}</span
        >
      </div>
    </div>
  </div>

  <!-- Special Site Condition — its own danger callout, independent of Table A. -->
  <div class="site-card__callout site-card__callout--danger" *ngIf="specialSiteCondition">
    <span class="material-icons">warning</span>
    <div>
      <p class="site-card__callout-title">Special Site Condition</p>
      <p class="site-card__callout-body">{{ specialSiteCondition }}</p>
    </div>
  </div>

  <div class="site-card__body" *ngIf="expanded">
    <app-product-line
      *ngFor="let product of site.products; let i = index; trackBy: trackByProduct"
      [product]="product"
      [type]="type"
      [projectId]="projectId"
      [rawProject]="rawProject"
      [siteNumber]="site.siteNumber"
      [baseTestId]="productTestIds[i]"
      [readOnly]="readOnly"
      (atpSigned)="atpSigned.emit($event)"
    ></app-product-line>
  </div>

  <mapmodal-dialog
    [active]="mapModalActive"
    [siteType]="type"
    [siteId]="site.site?.id"
    [data]="{ site: site.site }"
    [address]="address"
    (modalClosed)="toggleMapModal()"
    [ngClass]="{ 'mapmodal-dialog--active': mapModalActive }"
  ></mapmodal-dialog>
</div>
